import { inject, injectable } from 'inversify'
import { action, makeObservable, observable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { AppLifecycle } from '@core/interfaces/app-lifecycle'
import type { LocalStorageGateway } from '@core/services/local-storage.gateway'
import type { FormatGateway } from '@core/services/format.gateway'
import type { DocumentGateway } from '@core/services/document.gateway'
import { Locales, TranslationsConfig, type Locale } from './i18n.config'

const STORAGE_KEY = 'locale'

/** Source of truth for the active locale and its persistence to storage. */
@injectable()
export class I18nRepository implements AppLifecycle {
  /**
   * Never null — see the note in ThemeRepository. Seeded from the static list
   * rather than `translationsConfig.fallbackLocale` only because @inject fields
   * aren't populated during field initialisation; restore() immediately
   * replaces this with the configured fallback, which is authoritative.
   */
  localePm: Locale = Locales[0]

  @inject(TranslationsConfig)
  private translationsConfig!: TranslationsConfig

  @inject(Types.ILocalStorageGateway)
  private localStorageGateway!: LocalStorageGateway

  @inject(Types.IFormatGateway)
  private formatGateway!: FormatGateway

  @inject(Types.IDocumentGateway)
  private documentGateway!: DocumentGateway

  constructor() {
    makeObservable(this, { localePm: observable })
  }

  // Bootstrap hook. Distinct from restore() only in intent: restore() is
  // "re-read storage", which tests also use to simulate a later boot.
  initialize = () => this.restore()

  restore = action(() => {
    this.localePm = this.translationsConfig.fallbackLocale

    const stored = this.localStorageGateway.getValue(STORAGE_KEY)
    // Untrusted: a locale we no longer ship would index the table as undefined
    // and blow up every t() call.
    if (typeof stored === 'string' && this.translationsConfig.isSupported(stored)) {
      this.localePm = stored
    }

    this.formatGateway.setLocale(this.localePm)
    this.documentGateway.setLang(this.localePm)
  })

  setLocale = action((locale: Locale) => {
    this.localePm = locale
    this.localStorageGateway.setValue(STORAGE_KEY, locale)
    this.formatGateway.setLocale(locale)
    this.documentGateway.setLang(locale)
  })

  /**
   * Resolves a key against the current locale. Lives here rather than on
   * I18nPresenter so that a feature presenter needing a translated string
   * injects a Repository, not another Presenter — it wants a lookup, not a
   * ViewModel (draft §O). Each feature still resolves its own strings into
   * its own ViewModel; this only supplies the lookup.
   *
   * Missing key in the current locale falls back to the fallback locale, then
   * to the raw key itself — never throws, never blanks out UI text.
   */
  t(key: string, params: Record<string, string | number> = {}): string {
    const { translations, fallbackLocale } = this.translationsConfig
    const template = translations[this.localePm][key] ?? translations[fallbackLocale][key] ?? key

    return template.replace(/{{(\w+)}}/g, (_match, token) => String(params[token] ?? `{{${token}}}`))
  }
}
